import React, { useEffect, useMemo, useState, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import { clientsAPI, usersAPI, opportunitiesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Loader2, MapPin, Users, Flame, Layers, Building2, Phone, Mail, Calendar as CalendarIcon, Crosshair, RefreshCw } from 'lucide-react';
import { getCountryCoord, resolveCountry } from '../lib/countryCoords';
import { ClusterLayer } from '../components/map/ClusterLayer';
import { toast } from 'sonner';

const STATUS_COLORS = {
    active:   '#10B981',   // verde
    inactive: '#6B7280',   // grigio
    lost:     '#EF4444',   // rosso
};

const STATUS_LABELS = {
    active: 'Attivo',
    inactive: 'Inattivo',
    lost: 'Perso',
};

// Deterministic jitter so multiple clienti nello stesso paese non si sovrappongano
const jitter = (seed, amplitude = 1.4) => {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
    const dx = ((h & 0xff) / 255 - 0.5) * amplitude;
    const dy = (((h >> 8) & 0xff) / 255 - 0.5) * amplitude;
    return { dx, dy };
};

// Heatmap layer (imperative Leaflet plugin)
const HeatmapLayer = ({ points, show }) => {
    const map = useMap();
    const layerRef = useRef(null);

    useEffect(() => {
        if (layerRef.current) {
            map.removeLayer(layerRef.current);
            layerRef.current = null;
        }
        if (show && points.length > 0) {
            // points: [lat, lng, intensity]
            layerRef.current = L.heatLayer(points, {
                radius: 35,
                blur: 25,
                maxZoom: 6,
                max: 1.0,
                gradient: { 0.2: '#3B82F6', 0.4: '#10B981', 0.6: '#FBBF24', 0.8: '#F97316', 1.0: '#DC2626' },
            }).addTo(map);
        }
        return () => {
            if (layerRef.current) {
                map.removeLayer(layerRef.current);
                layerRef.current = null;
            }
        };
    }, [show, points, map]);

    return null;
};

const MapPage = () => {
    const { isAdmin } = useAuth();
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [repFilter, setRepFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [viewMode, setViewMode] = useState('pins'); // 'pins' | 'heat' | 'both'
    const [geo, setGeo] = useState(null);

    const loadGeoStatus = async () => {
        if (!isAdmin()) return;
        try { const { data } = await clientsAPI.geocodeStatus(); setGeo(data); } catch (e) { /* non admin */ }
    };
    useEffect(() => {
        loadGeoStatus();
        const t = setInterval(loadGeoStatus, 15000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGeocodeRun = async () => {
        try {
            const { data } = await clientsAPI.geocodeRun();
            toast[data.started ? 'success' : 'info'](data.started ? 'Geocodifica avviata in background' : data.message);
            loadGeoStatus();
        } catch (e) {
            toast.error('Impossibile avviare la geocodifica');
        }
    };

    useEffect(() => {
        const load = async () => {
            try {
                const reqs = [clientsAPI.getAll(), opportunitiesAPI.getAll()];
                if (isAdmin()) reqs.push(usersAPI.getAll());
                const [cRes, oRes, uRes] = await Promise.all(reqs);
                setClients(cRes.data);
                setOpportunities(oRes.data);
                if (uRes) setUsers(uRes.data);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [isAdmin]);

    const userById = useMemo(() => {
        const m = {};
        users.forEach(u => { m[u.id] = u; });
        return m;
    }, [users]);

    const pipelineByClient = useMemo(() => {
        const m = {};
        opportunities.forEach(o => {
            if (!m[o.client_id]) m[o.client_id] = 0;
            if (o.stage !== 'lost') m[o.client_id] += (o.value || 0);
        });
        return m;
    }, [opportunities]);

    const filteredClients = useMemo(() => {
        return clients.filter(c => {
            if (repFilter !== 'all' && c.assigned_to !== repFilter) return false;
            if (statusFilter !== 'all' && c.status !== statusFilter) return false;
            return true;
        });
    }, [clients, repFilter, statusFilter]);

    // Aggregations by country for legend & heatmap
    const MAX_PINS = 5000;
    const { countryAggregates, heatPoints, mapPoints, totalPoints, unlocated } = useMemo(() => {
        const agg = {};
        const heat = [];
        const points = [];
        const unknown = {};
        filteredClients.forEach(c => {
            const key = resolveCountry(c.country);
            const coord = getCountryCoord(c.country);
            if (!key || !coord) {
                const k = (c.country || '').trim() || 'Senza paese';
                unknown[k] = (unknown[k] || 0) + 1;
                return;
            }
            const label = coord.label || key;
            if (!agg[label]) agg[label] = { count: 0, value: 0, coord };
            agg[label].count += 1;
            agg[label].value += pipelineByClient[c.id] || 0;

            const precise = typeof c.lat === 'number' && typeof c.lng === 'number' && c.geo_precision && c.geo_precision !== 'none';
            let lat, lng;
            if (precise) {
                const { dx, dy } = jitter(c.id, c.geo_precision === 'city' ? 0.02 : 0.002);
                lat = c.lat + dy; lng = c.lng + dx;
            } else {
                const { dx, dy } = jitter(c.id);
                lat = coord.lat + dy; lng = coord.lng + dx;
            }
            const intensity = Math.min(1, 0.3 + (pipelineByClient[c.id] || 0) / 200000);
            heat.push([lat, lng, intensity]);
            points.push({ client: c, lat, lng, value: pipelineByClient[c.id] || 0, precise });
        });
        const total = points.length;
        // Cap rendered pins (Leaflet perf): keep highest-value clients
        const capped = total > MAX_PINS
            ? [...points].sort((a, b) => b.value - a.value).slice(0, MAX_PINS)
            : points;
        return { countryAggregates: agg, heatPoints: heat, mapPoints: capped, totalPoints: total, unlocated: unknown };
    }, [filteredClients, pipelineByClient]);
    const unlocatedCount = Object.values(unlocated).reduce((a, b) => a + b, 0);

    const formatEuro = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
    const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('it-IT') : '—';

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const showPins = viewMode === 'pins' || viewMode === 'both';
    const showHeat = viewMode === 'heat' || viewMode === 'both';

    const sortedCountries = Object.entries(countryAggregates).sort((a, b) => b[1].count - a[1].count);

    return (
        <div className="space-y-6" data-testid="map-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl uppercase tracking-tight">Mappa Clienti</h1>
                    <p className="text-muted-foreground">
                        {totalPoints} clienti in mappa · {sortedCountries.length} paesi{unlocatedCount > 0 ? ` · ${unlocatedCount} non geolocalizzati` : ''}
                        {showPins && (
                            <span className="block text-xs text-muted-foreground">
                                I clienti vicini sono raggruppati in bolle numerate: clicca una bolla o fai zoom per aprirla.
                            </span>
                        )}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant={viewMode === 'pins' ? 'default' : 'outline'}
                        onClick={() => setViewMode('pins')}
                        className={viewMode === 'pins' ? 'btn-primary' : ''}
                        data-testid="map-view-pins"
                    >
                        <MapPin size={16} className="mr-2" /> Pin
                    </Button>
                    <Button
                        variant={viewMode === 'heat' ? 'default' : 'outline'}
                        onClick={() => setViewMode('heat')}
                        className={viewMode === 'heat' ? 'btn-primary' : ''}
                        data-testid="map-view-heat"
                    >
                        <Flame size={16} className="mr-2" /> Heatmap
                    </Button>
                    <Button
                        variant={viewMode === 'both' ? 'default' : 'outline'}
                        onClick={() => setViewMode('both')}
                        className={viewMode === 'both' ? 'btn-primary' : ''}
                        data-testid="map-view-both"
                    >
                        <Layers size={16} className="mr-2" /> Entrambi
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
                    {isAdmin() && (
                        <div className="flex-1">
                            <Label className="text-xs uppercase tracking-wider mb-2 block">Commerciale</Label>
                            <Select value={repFilter} onValueChange={setRepFilter}>
                                <SelectTrigger data-testid="map-filter-rep">
                                    <SelectValue placeholder="Tutti" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti i commerciali</SelectItem>
                                    {users.filter(u => u.role !== 'addetto_consegna').map(u => (
                                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div className="flex-1">
                        <Label className="text-xs uppercase tracking-wider mb-2 block">Stato</Label>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger data-testid="map-filter-status">
                                <SelectValue placeholder="Tutti" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti gli stati</SelectItem>
                                <SelectItem value="active">Attivi</SelectItem>
                                <SelectItem value="inactive">Inattivi</SelectItem>
                                <SelectItem value="lost">Persi</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Map */}
                <Card className="lg:col-span-3 overflow-hidden">
                    <CardContent className="p-0">
                        <div style={{ height: '620px', width: '100%' }} data-testid="leaflet-map">
                            <MapContainer
                                center={[45, 15]}
                                zoom={3}
                                minZoom={2}
                                scrollWheelZoom={true}
                                worldCopyJump={true}
                                preferCanvas={true}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <HeatmapLayer points={heatPoints} show={showHeat} />
                                {showPins && <ClusterLayer points={mapPoints} renderPoint={({ client, lat, lng, value }) => (
                                    <CircleMarker
                                        key={client.id}
                                        center={[lat, lng]}
                                        radius={Math.max(7, Math.min(18, 7 + Math.log10((value || 1)) * 2))}
                                        pathOptions={{
                                            color: '#ffffff',
                                            weight: 2,
                                            fillColor: STATUS_COLORS[client.status] || '#6B7280',
                                            fillOpacity: client.geo_precision === 'address' ? 0.9 : client.geo_precision === 'city' ? 0.75 : 0.45,
                                        }}
                                    >
                                        <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                                            <span className="font-medium">{client.company_name}</span>
                                            <span className="text-xs text-muted-foreground"> · {client.country}{client.geo_precision === 'address' ? '' : client.geo_precision === 'city' ? ' · città' : ' · posizione approssimativa'}</span>
                                        </Tooltip>
                                        <Popup>
                                            <div className="space-y-2 min-w-[220px]" data-testid={`map-popup-${client.id}`}>
                                                <div className="flex items-center gap-2">
                                                    <Building2 size={16} />
                                                    <span className="font-heading uppercase text-sm">{client.company_name}</span>
                                                </div>
                                                <div className="text-xs text-muted-foreground space-y-1">
                                                    <div>Referente: <b>{client.contact_name}</b></div>
                                                    {client.email && <div className="flex items-center gap-1"><Mail size={12} /> {client.email}</div>}
                                                    {client.phone && <div className="flex items-center gap-1"><Phone size={12} /> {client.phone}</div>}
                                                    <div className="flex items-center gap-1"><MapPin size={12} /> {client.country}{client.address ? ` · ${client.address}` : ''}</div>
                                                    {client.first_contact_place && (
                                                        <div className="flex items-start gap-1 pt-1 border-t">
                                                            <CalendarIcon size={12} className="mt-0.5" />
                                                            <span>Primo contatto: <b>{client.first_contact_place}</b>{client.first_contact_date ? ` · ${formatDate(client.first_contact_date)}` : ''}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center justify-between pt-1">
                                                    <Badge
                                                        style={{ backgroundColor: STATUS_COLORS[client.status], color: 'white' }}
                                                    >
                                                        {STATUS_LABELS[client.status] || client.status}
                                                    </Badge>
                                                    <span className="text-xs font-medium">{formatEuro(value)}</span>
                                                </div>
                                                {isAdmin() && userById[client.assigned_to] && (
                                                    <div className="text-xs text-muted-foreground border-t pt-1">
                                                        Commerciale: <b>{userById[client.assigned_to].name}</b>
                                                    </div>
                                                )}
                                            </div>
                                        </Popup>
                                    </CircleMarker>
                                )} />}
                            </MapContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Sidebar legend */}
                <div className="space-y-6">
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
                                <Layers size={16} /> Legenda Stato
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                            {Object.entries(STATUS_LABELS).map(([key, label]) => (
                                <div key={key} className="flex items-center gap-3">
                                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[key] }} />
                                    <span>{label}</span>
                                    <span className="ml-auto text-xs text-muted-foreground">
                                        {filteredClients.filter(c => c.status === key).length}
                                    </span>
                                </div>
                            ))}
                            <div className="pt-2 text-xs text-muted-foreground">
                                Dimensione pin proporzionale al valore pipeline
                            </div>
                        </CardContent>
                    </Card>

                    {isAdmin() && geo && (
                        <Card data-testid="map-geocode-card">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
                                    <Crosshair size={16} /> Precisione indirizzi
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 text-sm">
                                <div className="flex justify-between"><span>Clienti geolocalizzati</span><b data-testid="geocode-located">{geo.clients_located} / {geo.clients_total}</b></div>
                                <div className="h-2 rounded-full bg-muted overflow-hidden">
                                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${geo.clients_total ? Math.round(geo.clients_located / geo.clients_total * 100) : 0}%` }} />
                                </div>
                                {geo.running ? (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="geocode-running">
                                        <Loader2 size={12} className="animate-spin" /> In corso: {geo.processed} / {geo.total} indirizzi
                                    </p>
                                ) : (
                                    <p className="text-xs text-muted-foreground">
                                        {geo.clients_never_geocoded > 0 ? `${geo.clients_never_geocoded} clienti ancora da elaborare.` : 'Tutti gli indirizzi sono stati elaborati.'}
                                        {' '}I pin trasparenti indicano posizione approssimativa (centro paese).
                                    </p>
                                )}
                                <Button variant="outline" size="sm" className="w-full" onClick={handleGeocodeRun} disabled={geo.running} data-testid="geocode-run-btn">
                                    <RefreshCw size={14} className="mr-2" /> {geo.running ? 'Elaborazione…' : 'Aggiorna posizioni'}
                                </Button>
                            </CardContent>
                        </Card>
                    )}

                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
                                <Users size={16} /> Top Paesi
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm max-h-[360px] overflow-y-auto">
                            {sortedCountries.length === 0 && (
                                <p className="text-muted-foreground text-xs">Nessun cliente da mostrare</p>
                            )}
                            {sortedCountries.map(([country, data]) => (
                                <div key={country} className="flex items-center justify-between gap-2 py-1 border-b last:border-0">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <MapPin size={12} className="text-muted-foreground shrink-0" />
                                        <span className="truncate">{country}</span>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <Badge variant="outline" className="text-xs">{data.count}</Badge>
                                        <span className="text-xs text-muted-foreground w-20 text-right">
                                            {formatEuro(data.value)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    {unlocatedCount > 0 && (
                        <Card data-testid="map-unlocated-card">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
                                    <MapPin size={16} /> Non geolocalizzati
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-1 text-sm">
                                <p className="text-xs text-muted-foreground mb-2">
                                    {unlocatedCount} clienti con paese non riconosciuto (non mostrati in mappa):
                                </p>
                                {Object.entries(unlocated).sort((a, b) => b[1] - a[1]).map(([name, n]) => (
                                    <div key={name} className="flex items-center justify-between gap-2 py-0.5">
                                        <span className="truncate">{name}</span>
                                        <Badge variant="outline" className="text-xs">{n}</Badge>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MapPage;
